import { useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { api, call } from '../lib/api'
import { meQuery } from '../lib/queries'
import { AuthButton, AuthInput, AuthLayout, FormError } from '../components/auth'

export const Route = createFileRoute('/login')({
  validateSearch: (s: Record<string, unknown>): { next?: string } => ({ next: typeof s.next === 'string' && s.next.startsWith('/') ? s.next : undefined }),
  component: Login,
})

function Login() {
  const { next } = Route.useSearch()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: React.SubmitEvent<HTMLFormElement>) {
    e.preventDefault()
    const f = new FormData(e.currentTarget)
    setBusy(true)
    setError(null)
    try {
      await call(api.auth.login.$post({ json: { email: String(f.get('email')), password: String(f.get('password')), remember: f.get('remember') === 'on' } }))
      qc.clear()
      const me = await qc.fetchQuery(meQuery)
      navigate({ to: me.business ? (next ?? '/') : next?.startsWith('/portal') ? next : '/portal' })
    } catch (err) {
      setError((err as Error).message)
      setBusy(false)
    }
  }

  return (
    <AuthLayout title="Sign In" subtitle="Enter your email and password to sign in!">
      <form onSubmit={submit} noValidate={false}>
        <FormError error={error} />
        <AuthInput label="Email" name="email" type="email" autoComplete="email" placeholder="enter your email address" required autoFocus />
        <AuthInput label="Password" name="password" type="password" autoComplete="current-password" placeholder="enter your password" required />
        <div className="mb-8 flex items-center justify-between gap-4 text-sm font-medium">
          <label className="flex items-center gap-3 text-ink-3">
            <input type="checkbox" name="remember" defaultChecked className="size-[18px] rounded-sm accent-brand" />
            Keep me logged in
          </label>
          <Link to="/forgot-password" className="text-brand hover:underline">Forget password?</Link>
        </div>
        <AuthButton busy={busy}>Sign In</AuthButton>
        <div className="mt-8 border-t border-black/10 pt-6 text-sm">
          <p className="mb-3 text-center text-ink-2">New to GasRail?</p>
          <div className="grid grid-cols-2 gap-3">
            <Link to="/signup" className="rounded-2xl border border-field bg-white px-4 py-3 text-center hover:border-brand"><b className="block text-ink">I buy gas</b><span className="text-xs text-ink-2">Track my cylinders</span></Link>
            <Link to="/register" className="rounded-2xl border border-field bg-white px-4 py-3 text-center hover:border-brand"><b className="block text-ink">I sell gas</b><span className="text-xs text-ink-2">Register a business</span></Link>
          </div>
        </div>
      </form>
    </AuthLayout>
  )
}
